"use client";

import { useEffect, useRef } from "react";

const COLOR_HEX: Record<string, string> = {
  red: "#DC2626",
  pink: "#EC4899",
  "hot-pink": "#DB2777",
  peach: "#FDBA74",
  white: "#F8FAFC",
  cream: "#FFF8DC",
  blue: "#4169E1",
  green: "#9CAF88",
  purple: "#9370DB",
  orange: "#F97316",
  yellow: "#EAB308",
  bicolor: "#FFD700",
  lavender: "#E6E6FA",
  "light-pink": "#F9A8D4",
  lilac: "#C4B5FD",
  brown: "#8B4513",
  sand: "#D2B48C",
  burgundy: "#800020",
  apricot: "#FBCEB1",
  picotee: "#F8F0E3",
};

const MIX_GRADIENT = "linear-gradient(90deg, #FEF3C7, #DBEAFE, #FCE7F3)";

function colorLabel(c: string): string {
  return c
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

// Chip rendered inline in the row. Click toggles the inline picker open.
export function ColorChipButton({
  active,
  onClick,
  testId = "power-buyer-row-color-chip",
}: {
  active: string; // a color slug or "mix"
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  testId?: string;
}) {
  const isMix = active === "mix";
  const dotStyle = isMix
    ? { background: MIX_GRADIENT }
    : { backgroundColor: COLOR_HEX[active] ?? "#ccc" };
  return (
    <button
      type="button"
      data-testid={testId}
      data-color={active}
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded border border-brand-teal bg-white px-2 py-0.5 text-[10px] font-bold text-brand-text hover:border-brand-teal-dark"
    >
      <span
        data-testid="power-buyer-row-color-chip-dot"
        className="inline-block h-3 w-3 rounded-full border border-black/10"
        style={dotStyle}
      />
      <span className="capitalize">{isMix ? "Mix" : colorLabel(active)}</span>
      <span aria-hidden="true" className="text-[8px] text-brand-text-muted">
        v
      </span>
    </button>
  );
}

// Anchored popover - closes on outside click and ESC. Positioned with
// absolute below its parent (caller should set position: relative on the
// row's chip column).
export function ColorPickerInline({
  options,
  active,
  allowMix,
  onPick,
  onClose,
  // #468 mobile Power Buyer: anchor the popover to the RIGHT edge of the chip so
  // it doesn't overflow the screen in the compact row (desktop keeps left).
  align = "left",
  testId = "color-picker-inline",
}: {
  options: string[];
  active: string;
  allowMix: boolean;
  onPick: (c: string) => void;
  onClose: () => void;
  align?: "left" | "right";
  testId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    // Defer one tick so the click that opened the popover doesn't
    // immediately close it.
    const t = setTimeout(() => {
      window.addEventListener("click", onDocClick);
    }, 0);
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("click", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid={testId}
      className={
        (align === "right" ? "right-0" : "left-0") +
        " absolute top-[calc(100%+4px)] z-20 flex min-w-[220px] flex-wrap items-center gap-1 rounded-lg border border-brand-border bg-white p-2.5 shadow-xl"
      }
    >
      <span className="mb-1 w-full text-[10px] font-bold uppercase tracking-wider text-brand-text-muted">
        Pick color
      </span>
      {options.map((c) => {
        const isActive = c === active;
        const dotStyle =
          c === "bicolor"
            ? { background: "linear-gradient(135deg, #FFD700 50%, #DC143C 50%)" }
            : { backgroundColor: COLOR_HEX[c] ?? "#ccc" };
        return (
          <button
            key={c}
            type="button"
            data-testid={`color-picker-inline-${c}`}
            aria-label={colorLabel(c)}
            aria-pressed={isActive}
            onClick={(e) => {
              e.stopPropagation();
              onPick(c);
            }}
            className={
              isActive
                ? "h-[22px] w-[22px] rounded-full ring-2 ring-brand-teal ring-offset-2 ring-offset-white"
                : "h-[22px] w-[22px] rounded-full border border-black/10"
            }
            style={dotStyle}
          />
        );
      })}
      {allowMix && (
        <button
          type="button"
          data-testid="color-picker-inline-mix"
          aria-pressed={active === "mix"}
          onClick={(e) => {
            e.stopPropagation();
            onPick("mix");
          }}
          className={
            active === "mix"
              ? "rounded border border-brand-border px-2 py-0.5 text-[10px] font-bold text-brand-text ring-2 ring-brand-teal"
              : "rounded border border-brand-border px-2 py-0.5 text-[10px] font-bold text-brand-text"
          }
          style={{ background: MIX_GRADIENT }}
        >
          + Mix
        </button>
      )}
    </div>
  );
}
